/* ---------------------------------------------------------------------------
   Named aliases over the generated contract types.

   `src/types/openapi.ts` is generated — `npm run gen:api` — from
   backend/api/openapi.yaml, which is shared contract C1 and the single source
   of truth for every payload, status enum, reason code, SSE kind and block code
   in this dashboard. Never hand-edit the generated file; edit the YAML and
   regenerate.

   This module exists so screen code reads `ExpertChat` instead of
   `components['schemas']['ExpertChat']`, and so a contract change surfaces here
   as a compile error in one file rather than as a hundred untyped property
   reads.
--------------------------------------------------------------------------- */
import type { components } from './openapi';

type S = components['schemas'];

/** Strips the `| null` a nullable contract schema carries, for the branches
    where we have already proved the value is present. */
export type Present<T> = Exclude<T, null | undefined>;

// -- envelope ---------------------------------------------------------------
export type Meta = S['Meta'];
export type FieldError = S['FieldError'];

// -- vocabulary -------------------------------------------------------------
export type Role = S['Role'];
export type ActorKind = S['ActorKind'];
export type ChatStatus = S['ChatStatus'];
export type ClientChatStatus = S['ClientChatStatus'];
export type ComposerReasonCode = S['ComposerReasonCode'];
export type Visibility = S['Visibility'];
export type MessageAuthorKind = S['MessageAuthorKind'];
export type ToolFamily = S['ToolFamily'];
export type RunStatus = S['RunStatus'];
export type RunPhase = S['RunPhase'];
export type DeployEnvironment = S['DeployEnvironment'];
export type DeployStatus = S['DeployStatus'];
export type PromotionTarget = S['PromotionTarget'];
export type PromotionBlockCode = S['PromotionBlockCode'];

// -- identity ---------------------------------------------------------------
export type UserIdentity = S['UserIdentity'];
export type Session = S['Session'];

// -- projects and queue -----------------------------------------------------
export type ExpertProjectSummary = S['ExpertProjectSummary'];
export type ExpertProjectRepo = ExpertProjectSummary['repos'] extends (infer R)[] | undefined ? R : never;
export type ReviewQueueItem = S['ReviewQueueItem'];
export type Claim = S['Claim'];
export type ClaimHolder = Present<Claim>;
export type QueueFilter = 'unclaimed' | 'mine' | 'all' | 'promotion_failed';

// -- chat -------------------------------------------------------------------
export type ExpertChat = S['ExpertChat'];
export type ChatMessage = S['ChatMessage'];
export type MessageBlock = S['MessageBlock'];
export type TextBlock = S['TextBlock'];
export type AskBlock = S['AskBlock'];
export type ActionBlock = S['ActionBlock'];
export type NoticeBlock = S['NoticeBlock'];
export type Attachment = S['Attachment'];
export type ComposerState = S['ComposerState'];
export type PromotionGate = Present<ExpertChat['promotion_gate']>;

// -- runs and deploys -------------------------------------------------------
export type RunSummary = Present<S['RunSummary']>;
export type RunRepo = S['RunRepo'];
export type PushStatus = RunRepo['push_status'];
export type Deploy = Present<S['Deploy']>;

// -- diff -------------------------------------------------------------------
export type AggregatedDiff = S['AggregatedDiff'];
export type RepoDiff = S['RepoDiff'];
export type DiffFile = S['DiffFile'];
export type DiffHunk = S['DiffHunk'];
export type DiffLine = S['DiffLine'];
export type DiffLineKind = DiffLine['kind'];
export type ChangeKind = DiffFile['change_kind'];

// -- promotion --------------------------------------------------------------
export type PromotionPreflight = S['PromotionPreflight'];
export type PromotionRepoPreflight = S['PromotionRepoPreflight'];
export type PreflightRepoStatus = PromotionRepoPreflight['status'];
export type AncestryCheck = Present<S['AncestryCheck']>;
export type Promotion = Present<S['Promotion']>;
export type PromotionStatus = Promotion['status'];
export type PromotionRepo = S['PromotionRepo'];
export type PromotionMergeStatus = PromotionRepo['merge_status'];
export type PromotionPreflightStatus = PromotionRepo['preflight_status'];
export type PromotionFailure = Present<S['PromotionFailure']>;

// -- request bodies ---------------------------------------------------------
export type PostMessageRequest = S['PostMessageRequest'];
export type AnswerAskRequest = S['AnswerAskRequest'];
export type PromoteRequest = S['PromoteRequest'];
export type RequestChangesRequest = S['RequestChangesRequest'];

// -- stream -----------------------------------------------------------------
export type SseExpertEvent = S['SseExpertEvent'];
export type SseExpertEventKind = S['SseExpertEventKind'];

/**
 * The exhaustive set of stream kinds this dashboard handles.
 *
 * It mirrors `SseExpertEventKind` from the contract, and exists as a runtime
 * value because a type cannot check an incoming frame. Anything outside this
 * set is contract drift: the expert stream is not filtered, so an unrecognised
 * kind is a build that is behind the contract rather than a scrub bug — but it
 * still must not be drawn optimistically, because this renderer decides what is
 * marked hidden from the client.
 */
export const EXPERT_EVENT_KINDS = [
  'stream_open',
  'stream_reset',
  'status',
  'run_started',
  'phase',
  'message',
  'text_delta',
  'thinking',
  'action',
  'action_result',
  'ask',
  'ask_resolved',
  'usage',
  'notice',
  'deploy',
  'error_code',
  'promotion',
  'run_finished',
  'run_failed',
] as const satisfies readonly SseExpertEventKind[];

const EXPERT_EVENT_KIND_SET: ReadonlySet<string> = new Set(EXPERT_EVENT_KINDS);

export function isKnownExpertEventKind(kind: string): kind is SseExpertEventKind {
  return EXPERT_EVENT_KIND_SET.has(kind);
}

/**
 * Block types this surface renders. Unlike the client, `action` IS in the set:
 * the expert is inside the trust boundary and the tool stream is the point.
 */
export const EXPERT_BLOCK_TYPES = ['text', 'ask', 'action', 'notice'] as const;
export type ExpertBlockType = (typeof EXPERT_BLOCK_TYPES)[number];

const EXPERT_BLOCK_TYPE_SET: ReadonlySet<string> = new Set(EXPERT_BLOCK_TYPES);

export function isExpertBlockType(type: string): type is ExpertBlockType {
  return EXPERT_BLOCK_TYPE_SET.has(type);
}

/**
 * The three visibilities, as a runtime set.
 *
 * This is the one lookup in the app that must never fall through to a default.
 * `client` means the client reads this turn; `expert` and `internal` mean they
 * cannot. A fourth value appearing here would make the renderer guess which
 * half of that sentence applies, and guessing wrong in either direction is the
 * failure this dashboard exists to prevent.
 */
export const VISIBILITIES = ['client', 'expert', 'internal'] as const satisfies readonly Visibility[];

const VISIBILITY_SET: ReadonlySet<string> = new Set(VISIBILITIES);

export function isKnownVisibility(value: string): value is Visibility {
  return VISIBILITY_SET.has(value);
}

/** True when the client can read this turn. Everything else is hidden. */
export function isClientVisible(visibility: Visibility): boolean {
  return visibility === 'client';
}
